// React
import {
  createContext,
  ReactNode, useEffect,
  useState
} from 'react';
import AzureAuth from "../azure-component/AzureAuth";
import { AccountInfo } from '@azure/msal-browser';
import { CLASSTER_TENANTS_BY_UUID } from 'src/@constants/tenants-constants';
import { SuperAdminUsersEmail } from "../@constants";
import { fetchUserRole } from 'src/services';

// ========================================================================== //
// ================================= LOGIC ================================== //
// ========================================================================== //

export type UserInfo = {
  role: string,
  permissions: string[],
  first_name: string,
  last_name: string,
  email: string,
  token: string,
  account: Record<string, any>,
  user_id: string,
  user_name: string,
}

export type AuthInterface = {
  isAuthenticated: () => boolean,
  user: UserInfo | null,
  tenant: Tenant | null,
  isSuperAdmin: boolean,
  logout: VoidFunction,
};

export type Tenant = {
  name: string,
  id: string,
}

/**
 * @description - Worlds base context to create context of world lifecycle
 */
export const ContextAuth = createContext<AuthInterface | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // The games visibility state variable
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);

  /**
   * @description Getting the role from the PROSYS platform
   *  available roles are
   *
   * ADMINISTRATOR = 1
   * GUEST = 2
   * AUTHENTICATED_USER = 3
   * STUDENT = 4
   * TEACHER = 5
   * INSTITUTE_DIRECTOR = 6  # Institute Director
   * COUNSELLOR = 7
   * ISSSC = 8  # Student Support Coordinator
   * IT_ADMIN = 9  # IT-Manager / Administrator
   * STUDENT_MENTOR = 10
   * DEPUTY_DIRECTOR_IEU = 11  # Deputy Director Inclusive Education Unit (IEU)
   * DIRECTOR_APPRENTICESHIP_LSU = 12  # Director of Apprenticeship / Deputy Director Learning Support Unit (LSU)  # noqa E501
   * RESEARCHER = 13  # Researcher / Assistant Researcher
   * PRINCIPAL = 14  # Principal / Deputy Principal
   * LEARNING_SUPPORT_EDUCATOR = 15
   * DEPUTY_INSTITUTE_DIRECTOR = 16  # Deput
   */
  const internalRoll = () => {
    if (account?.username?.toLowerCase()?.includes('researcher')) {
      return 'administrator';
    }
    if (account?.username?.toLowerCase()?.includes('lecturer')) {
      return 'lecturer'
    }
    if (account?.username?.toLowerCase()?.includes('mentor')) {
      return 'mentor'
    }
    if (account?.username?.toLowerCase()?.includes('advisor')) {
      return 'advisor'
    }
    if (account?.username?.toLowerCase()?.includes('counsellor')) {
      return 'counsellor'
    }
    return 'student'
  }
  const getRole = async () => {
    // const userRole = await fetchUserRole(token);
    // ToDo remove dummy data and bring the prev comments back
    // console.log(account);
    return {
      user_id: account?.localAccountId,
      role: internalRoll(),
      permissions: [],
      first_name: account?.name,
      email: account?.username
    };
  }

  const isAuthenticated = () => {
    return AzureAuth?.isLoggedIn() || false;
  }

  const logout = () => {
    AzureAuth.logout();
  }

  useEffect(() => {
    if (!account) {
      setAccount(AzureAuth.getAccount());
    }
  }, []);

  useEffect(() => {
    if (account) {
      AzureAuth
        .getToken()
        .then((tokenData) => typeof tokenData === 'string' ? setToken(tokenData) : null)
        .catch(console.log);
    }
  }, [account]);

  useEffect(() => {
    if (token && !user && account) {
      getRole()
        .then((roleData: Record<string, any>) => {
          setUser({
            user_id: roleData?.user_id ?? 'na',
            role: roleData?.role ?? 'na',
            permissions: roleData?.permissions ?? [],
            user_name: account?.name ?? 'na',
            first_name: roleData?.first_name ?? 'na',
            last_name: roleData?.first_name ?? 'na',
            email: roleData?.email ?? 'na',
            account,
            token,
          });
          if (SuperAdminUsersEmail.includes(roleData?.email?.toLowerCase())) {
            setIsSuperAdmin(true);
          }
        })
        .catch(console.error);

      if (SuperAdminUsersEmail.includes(account?.username?.toLowerCase())) {
        setIsSuperAdmin(true);
      }
    }
  }, [token]);

  useEffect(() => {
    if (!tenant && user && typeof user?.account?.tenantId === "string") {
      const user_tenant = CLASSTER_TENANTS_BY_UUID[user.account.tenantId];
      if (user_tenant) {
        setTenant({
          name: user_tenant,
          id: user.account.tenantId
        })
      }
    }
  }, [user]);

  return (
    <ContextAuth.Provider
      value={{
        isAuthenticated,
        user,
        tenant,
        isSuperAdmin,
        logout
      }}
    >
      {children}
    </ContextAuth.Provider>
  );
}
